import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  useLazyGetApiProgramsDeliverableTypesQuery,
  useLazyGetApiProjectsDeliverableTypesQuery,
} from '../../../../app/athenaApiGen';
import { setFormData, setOriginalDeliverableTypeIds, setOriginalDeliverableTypes } from '../../../../app/projects/projectsSlice';

/**
 * DeliverableTypesForm - Step for selecting deliverable types for a project
 *
 * Sources the picker list from the parent program's enabled deliverable types
 * (GET /api/programs/deliverable-types?programId=...), so users can only pick
 * options the backend will accept. Switching the parent program in a prior step
 * invalidates the current picks; on return to this step the stale selection is
 * cleared and the user is notified via a Snackbar.
 *
 * Edit mode: the project's currently-saved deliverable types are loaded via
 * GET /api/projects/{projectId}/deliverable-types. Any saved type whose
 * deliverableTypeId is no longer in the program's current list is rendered as
 * an "orphan" row — disabled, pre-checked, with a warning chip — and is
 * preserved in the submission payload.
 */
export default function DeliverableTypesForm() {
  const dispatch = useDispatch();
  const { formData, modalMode, editingProjectId, originalDeliverableTypes } = useSelector((state) => state.projects);

  const [fetchProgramTypes, { data: programTypes, isLoading, isError }] =
    useLazyGetApiProgramsDeliverableTypesQuery();

  const [fetchProjectDeliverableTypes, { data: projectDeliverableTypes }] =
    useLazyGetApiProjectsDeliverableTypesQuery();

  const [selectedTypes, setSelectedTypes] = React.useState(
    formData.selectedDeliverableTypes || []
  );

  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'info' });

  // Effect A: fetch the program-scoped list whenever the parent programId is set.
  React.useEffect(() => {
    if (formData.programId) {
      fetchProgramTypes({ programId: formData.programId });
    }
  }, [formData.programId, fetchProgramTypes]);

  // Effect B: in edit mode, fetch existing deliverable types for this project (baseline).
  React.useEffect(() => {
    if (modalMode === 'edit' && editingProjectId) {
      fetchProjectDeliverableTypes({
        projectId: editingProjectId,
      });
    }
  }, [modalMode, editingProjectId, fetchProjectDeliverableTypes]);

  // Track if we've already saved the original types (to avoid overwriting on re-renders)
  const hasSetOriginalRef = React.useRef(false);

  // Effect C: reset the original-baseline ref when modal mode changes away from edit.
  React.useEffect(() => {
    if (modalMode !== 'edit') {
      hasSetOriginalRef.current = false;
    }
  }, [modalMode]);

  // Effect D: populate selection from the edit-mode baseline once.
  React.useEffect(() => {
    if (modalMode === 'edit' && projectDeliverableTypes && projectDeliverableTypes.length > 0) {
      const existingTypeIds = projectDeliverableTypes.map(dt => dt.deliverableTypeId);
      setSelectedTypes(existingTypeIds);
      dispatch(setFormData({ selectedDeliverableTypes: existingTypeIds }));

      if (!hasSetOriginalRef.current) {
        dispatch(setOriginalDeliverableTypeIds(existingTypeIds));
        dispatch(setOriginalDeliverableTypes(projectDeliverableTypes));
        hasSetOriginalRef.current = true;
      }
    }
  }, [projectDeliverableTypes, modalMode, dispatch]);

  // Deduplicate program deliverable types by deliverableTypeId (first-wins).
  // The program's table is keyed by (programId, deliverableTypeId, deliverableNumber),
  // so multiple rows can share a deliverableTypeId.
  const uniqueDeliverableTypes = React.useMemo(() => {
    if (!Array.isArray(programTypes)) return [];

    const seen = new Map();
    programTypes.forEach((item) => {
      if (!seen.has(item.deliverableTypeId)) {
        seen.set(item.deliverableTypeId, item);
      }
    });
    return Array.from(seen.values());
  }, [programTypes]);

  // Orphan rows (edit mode only): saved on this project but no longer on the program.
  const orphanTypes = React.useMemo(() => {
    if (modalMode !== 'edit') return [];
    if (!Array.isArray(originalDeliverableTypes) || originalDeliverableTypes.length === 0) return [];

    const programTypeIds = new Set(uniqueDeliverableTypes.map(t => t.deliverableTypeId));
    return originalDeliverableTypes.filter(
      (dt) => dt.deliverableTypeId && !programTypeIds.has(dt.deliverableTypeId)
    );
  }, [modalMode, originalDeliverableTypes, uniqueDeliverableTypes]);

  // Effect E: reset the stale-selection-validated ref when the program changes,
  // so the validation in Effect F runs again against the new program's list.
  const hasValidatedRef = React.useRef(false);
  React.useEffect(() => {
    hasValidatedRef.current = false;
  }, [formData.programId]);

  // Effect F: stale-selection cleanup (create mode only).
  // When the program list arrives, if any selected id isn't in the program's
  // valid set, clear the entire selection and toast the user. Edit-mode
  // orphans are intentional and preserved.
  React.useEffect(() => {
    if (modalMode === 'edit') return;
    if (!Array.isArray(programTypes)) return;
    if (hasValidatedRef.current) return;

    const validIds = new Set(programTypes.map(t => t.deliverableTypeId));
    const hasStale = selectedTypes.some((id) => !validIds.has(id));
    if (hasStale) {
      setSelectedTypes([]);
      setSnackbar({
        open: true,
        message: 'Program changed — deliverable-type selection cleared',
        severity: 'info',
      });
    }
    hasValidatedRef.current = true;
  }, [programTypes, modalMode, selectedTypes]);

  // Effect G: surface fetch errors via Snackbar.
  React.useEffect(() => {
    if (isError) {
      setSnackbar({
        open: true,
        message: "Couldn't load this program's deliverable types",
        severity: 'error',
      });
    }
  }, [isError]);

  // Effect H: keep Redux in sync with local selection state.
  React.useEffect(() => {
    dispatch(setFormData({ selectedDeliverableTypes: selectedTypes }));
  }, [selectedTypes, dispatch]);

  const handleToggle = (deliverableTypeId) => {
    const currentIndex = selectedTypes.indexOf(deliverableTypeId);
    const newSelectedTypes = [...selectedTypes];

    if (currentIndex === -1) {
      newSelectedTypes.push(deliverableTypeId);
    } else {
      newSelectedTypes.splice(currentIndex, 1);
    }

    setSelectedTypes(newSelectedTypes);
  };

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const hasProgramId = Boolean(formData.programId);
  const programTypesLoaded = Array.isArray(programTypes);
  const renderListItems = uniqueDeliverableTypes.length + orphanTypes.length > 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Select Deliverable Types
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose which deliverable types should be associated with this project.
          Options come from the parent program's enabled deliverable types.
        </Typography>
      </Box>

      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', maxHeight: 300, overflow: 'auto' }}>
        {!hasProgramId ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Select a program in the previous step to see its deliverable types.
            </Typography>
          </Box>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Couldn't load deliverable types.
            </Typography>
          </Box>
        ) : renderListItems ? (
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {uniqueDeliverableTypes.map((item, index) => {
              const labelId = `checkbox-list-label-${index}`;
              const isChecked = selectedTypes.indexOf(item.deliverableTypeId) !== -1;

              const showDivider =
                index < uniqueDeliverableTypes.length - 1 || orphanTypes.length > 0;

              return (
                <React.Fragment key={item.deliverableTypeId}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Chip
                        label={item.deliverableType?.abbreviation || '-'}
                        size="small"
                        color={isChecked ? 'primary' : 'default'}
                      />
                    }
                  >
                    <ListItemButton role={undefined} onClick={() => handleToggle(item.deliverableTypeId)} dense>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={isChecked}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={labelId}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={`Del #${item.deliverableNumber || '-'}`}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body1" component="span" fontWeight={500}>
                              {item.deliverableType?.title || '-'}
                            </Typography>
                          </Box>
                        }
                        secondary={item.deliverableType?.description || '-'}
                      />
                    </ListItemButton>
                  </ListItem>
                  {showDivider && <Divider />}
                </React.Fragment>
              );
            })}

            {orphanTypes.map((item, orphanIndex) => {
              const labelId = `checkbox-list-orphan-label-${orphanIndex}`;
              return (
                <React.Fragment key={`orphan-${item.deliverableTypeId}`}>
                  <ListItem
                    disablePadding
                    aria-disabled="true"
                    secondaryAction={
                      <Chip
                        label="Not in program"
                        size="small"
                        color="warning"
                        aria-label="Deliverable type no longer offered by program"
                      />
                    }
                  >
                    <ListItemButton role={undefined} disabled dense>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked
                          disabled
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={labelId}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={`Del #${item.deliverableNumber || '-'}`}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body1" component="span" fontWeight={500}>
                              {item.deliverableType?.title || '-'}
                            </Typography>
                          </Box>
                        }
                        secondary={item.deliverableType?.description || '-'}
                      />
                    </ListItemButton>
                  </ListItem>
                  {orphanIndex < orphanTypes.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        ) : programTypesLoaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              This program has no deliverable types configured. Ask an admin to enable some in the Program admin module.
            </Typography>
          </Box>
        ) : null}
      </Paper>

      {selectedTypes.length > 0 && (
        <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: 'primary.contrastText' }}>
            <strong>{selectedTypes.length}</strong> deliverable type{selectedTypes.length !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
